import type { Icons } from "@/components/Icons";
import type { ProductBrand } from "@/lib/branding";

export type IconName = keyof typeof Icons;
export type Role = "member" | "manager" | "admin" | "owner";
export type AreaId = "home" | "water" | "fleet" | "people" | "support" | "apps" | "admin";
export type BadgeKey = "support-mine" | "support-inbox" | "water-ncs" | "water-orphans";

/** Who may see an entry. All set fields must pass; super admins pass everything but `superAdmin`. */
export interface Gate {
  feature?: string;
  permission?: string;
  minRole?: Role;
  superAdmin?: boolean;
}

export interface Tab extends Gate {
  label: string;
  path: string;
}

export interface Item extends Gate {
  id: string;
  label: string;
  /** Must equal one of `tabs`' paths when `tabs` is set. */
  path: string;
  icon: IconName;
  /** Extra path prefixes that keep this entry active. */
  matches?: string[];
  tabs?: Tab[];
  /** Served by the backend (Horizon); opened as a normal link in a new tab. */
  external?: boolean;
  badge?: BadgeKey;
}

export interface Group {
  label?: string;
  items: Item[];
}

export interface Area extends Gate {
  id: AreaId;
  label: string;
  icon: IconName;
  brand: ProductBrand;
  /** Rendered at the bottom of the rail (Admin). */
  pinBottom?: boolean;
  groups: Group[];
}

export interface NavContext {
  has: (feature: string) => boolean;
  permissions: readonly string[];
  role: string;
  isSuperAdmin: boolean;
}

export interface Active {
  area: Area;
  item: Item;
}

const ROLE_RANK: Record<string, number> = { member: 1, manager: 2, admin: 3, owner: 4 };

export function passes(g: Gate, ctx: NavContext): boolean {
  if (g.superAdmin && !ctx.isSuperAdmin) return false;
  if (ctx.isSuperAdmin) return true;
  if (g.feature && !ctx.has(g.feature)) return false;
  if (g.permission && !ctx.permissions.includes(g.permission)) return false;
  if (g.minRole && (ROLE_RANK[ctx.role] ?? 0) < ROLE_RANK[g.minRole]) return false;
  return true;
}

function visibleItem(item: Item, ctx: NavContext): Item | null {
  if (!passes(item, ctx)) return null;
  if (!item.tabs) return item;
  const tabs = item.tabs.filter((t) => passes(t, ctx));
  if (tabs.length === 0) return null;
  const path = tabs.some((t) => t.path === item.path) ? item.path : tabs[0].path;
  return { ...item, path, tabs };
}

export function visibleNav(areas: readonly Area[], ctx: NavContext): Area[] {
  const out: Area[] = [];
  for (const area of areas) {
    if (!passes(area, ctx)) continue;
    const groups = area.groups
      .map((g) => ({ ...g, items: g.items.map((i) => visibleItem(i, ctx)).filter((i): i is Item => i !== null) }))
      .filter((g) => g.items.length > 0);
    if (groups.length > 0) out.push({ ...area, groups });
  }
  return out;
}

export function pathMatches(pattern: string, pathname: string): boolean {
  if (pattern === "/") return pathname === "/";
  return pathname === pattern || pathname.startsWith(pattern + "/");
}

// Longest matching prefix wins; an item's own path beats a tab or `matches`
// entry of the same length, so /hr/time goes to Team › Time when visible.
export function activeItem(areas: readonly Area[], pathname: string): Active | null {
  let best: Active | null = null;
  let bestScore = -1;
  for (const area of areas) {
    for (const group of area.groups) {
      for (const item of group.items) {
        if (item.external) continue;
        const candidates: Array<[string, number]> = [
          [item.path, 0.5],
          ...(item.tabs ?? []).map((t): [string, number] => [t.path, 0]),
          ...(item.matches ?? []).map((m): [string, number] => [m, 0]),
        ];
        for (const [pattern, bonus] of candidates) {
          if (!pathMatches(pattern, pathname)) continue;
          const score = pattern.length + bonus;
          if (score > bestScore) {
            bestScore = score;
            best = { area, item };
          }
        }
      }
    }
  }
  return best;
}

export function activeTab(item: Item, pathname: string): Tab | null {
  let best: Tab | null = null;
  for (const t of item.tabs ?? []) {
    if (pathMatches(t.path, pathname) && (!best || t.path.length > best.path.length)) best = t;
  }
  return best;
}

export function firstPath(area: Area): string {
  return area.groups[0]?.items.find((i) => !i.external)?.path ?? "/";
}

export const BOTTOM_BAR_SLOTS = 5;

export function bottomBarSlots(areas: readonly Area[]): { primary: Area[]; more: Area[] } {
  if (areas.length <= BOTTOM_BAR_SLOTS) return { primary: [...areas], more: [] };
  return { primary: areas.slice(0, BOTTOM_BAR_SLOTS - 1), more: areas.slice(BOTTOM_BAR_SLOTS - 1) };
}
